import { Flag, Rocket, Lock, Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppContext } from "@/lib/onboardingContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";

export type AppMode = "opening" | "operations";

interface ModeSwitchProps {
  value: AppMode;
  onChange: (mode: AppMode) => void;
}

export default function ModeSwitch({ value, onChange }: ModeSwitchProps) {
  const { isOperationsUnlocked, manuallyOpened, setManuallyOpened, onboarding } = useAppContext();

  const handleOperationsClick = () => {
    if (isOperationsUnlocked) {
      onChange("operations");
      return;
    }
    // Locked — offer manual unlock
    const target = onboarding.targetOpeningDate;
    const targetLabel = target ? format(new Date(target), "MMM d, yyyy") : null;
    const ok = window.confirm(
      targetLabel
        ? `Operations mode unlocks automatically on ${targetLabel}.\n\nMark your business as open now to unlock it early?`
        : "Operations mode unlocks once your business is open.\n\nMark it as open now?",
    );
    if (ok) {
      setManuallyOpened(true);
      onChange("operations");
      toast.success("Operations mode unlocked");
    }
  };

  const operationsLocked = !isOperationsUnlocked;

  const operationsButton = (
    <button
      onClick={handleOperationsClick}
      aria-pressed={value === "operations"}
      className={`relative flex items-center gap-1.5 px-3 sm:px-4 h-8 rounded-md text-xs sm:text-sm font-medium transition-all ${
        value === "operations"
          ? "bg-card text-foreground shadow-sm border border-border/60"
          : operationsLocked
          ? "text-muted-foreground/70 hover:text-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-card/60"
      }`}
    >
      {operationsLocked ? <Lock className="w-3.5 h-3.5" /> : <Rocket className="w-3.5 h-3.5" />}
      <span>Operations</span>
    </button>
  );

  return (
    <div className="inline-flex items-center gap-1 rounded-lg bg-muted/60 p-1 border border-border/40">
      <button
        onClick={() => onChange("opening")}
        aria-pressed={value === "opening"}
        className={`flex items-center gap-1.5 px-3 sm:px-4 h-8 rounded-md text-xs sm:text-sm font-medium transition-all ${
          value === "opening"
            ? "bg-card text-foreground shadow-sm border border-border/60"
            : "text-muted-foreground hover:text-foreground hover:bg-card/60"
        }`}
      >
        <Flag className="w-3.5 h-3.5" />
        <span>Opening Plan</span>
      </button>

      {operationsLocked ? (
        <Tooltip>
          <TooltipTrigger asChild>{operationsButton}</TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs">
            {onboarding.targetOpeningDate
              ? `Unlocks ${format(new Date(onboarding.targetOpeningDate), "MMM d, yyyy")}. Click to mark as open now.`
              : "Unlocks once your business is open. Click to mark as open now."}
          </TooltipContent>
        </Tooltip>
      ) : (
        operationsButton
      )}

      {manuallyOpened && value === "operations" && (
        <span className="hidden sm:inline-flex items-center gap-1 ml-1 px-2 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
          <Check className="w-3 h-3" /> Open
        </span>
      )}
    </div>
  );
}
